import { FC, useMemo } from 'react'
import { PrescricaoType } from '_metronic/helpers'
import { PreferenciasSus } from '../../core/documentosSus'
import { DocumentoPrevisto, necessidadesSncr } from '../../core/montagem/documentos'
import { PreferenciasEnvio, pendenciasDeEnvio } from '../../core/montagem/envio'
import { ItemPrescricao, rotuloCatalogo } from '../../core/montagem/itens'
import { PendenciaItem } from '../../hooks/useMontagemPrescricao'
import { useSaldoSncr } from '../../hooks/useSaldoSncr'
import styles from '../../Prescricao.module.scss'
import { useRotulos } from '../comum/useRotulos'
import { isNotificacaoSncr } from '../../../../sncr/core/_models'
import { ID_EMAIL_ENVIO, ID_TELEFONE_ENVIO } from './ids'
import { SecaoEnvio } from './SecaoEnvio'
import { PendenciaRevisao, SecaoPendencias } from './SecaoPendencias'
import { SecaoDocumentos } from './SecaoDocumentos'
import { SecaoSncr } from './SecaoSncr'
import { SecaoSus } from './SecaoSus'
import { Button, Drawer } from 'app/components'

type IProps = {
  aberto: boolean
  /** `false` quando a saída leva o foco a um campo da montagem (pendência): o drawer não o rouba. */
  devolverFoco: boolean
  itens: ItemPrescricao[]
  documentos: DocumentoPrevisto[]
  pendenciasItens: PendenciaItem[]
  atendeSUS: boolean
  idEmpresaSncr?: number
  envio: PreferenciasEnvio
  onEnvioChange: (envio: PreferenciasEnvio) => void
  sus: PreferenciasSus
  onSusChange: (sus: PreferenciasSus) => void
  onIrParaItem: (pendencia: PendenciaItem) => void
  onFechar: () => void
  onAssinar: () => void
}

const focar = (id: string) => document.getElementById(id)?.focus()

/**
 * Revisão antes de assinar: pendências (cada uma leva ao campo; a de SNCR já abre a retirada, que
 * é a única saída), documentos que serão gerados, saldo SNCR, quando gerar LME/APAC e envio ao
 * paciente. Nada é gravado aqui. O botão de assinar não age enquanto houver pendência e, se
 * clicado, leva à primeira.
 */
export const RevisaoDrawer: FC<IProps> = ({
  aberto,
  devolverFoco,
  itens,
  documentos,
  pendenciasItens,
  atendeSUS,
  idEmpresaSncr,
  envio,
  onEnvioChange,
  sus,
  onSusChange,
  onIrParaItem,
  onFechar,
  onAssinar,
}) => {
  const { t, rotuloCampo } = useRotulos()
  const necessidades = useMemo(() => necessidadesSncr(documentos), [documentos])
  const saldo = useSaldoSncr(idEmpresaSncr, necessidades)
  const faltasEnvio = pendenciasDeEnvio(envio)

  const itensLme = atendeSUS
    ? itens
        .filter((item) => item.categoria === PrescricaoType.Medicamento && item.catalogo.lme)
        .map((item) => rotuloCatalogo(item.catalogo))
    : []
  const itensApac = atendeSUS
    ? itens
        .filter((item) => item.categoria === PrescricaoType.Exame && item.catalogo.apac)
        .map((item) => item.catalogo.nome)
    : []

  const pendencias: PendenciaRevisao[] = [
    ...pendenciasItens.map((pendencia) => ({
      id: pendencia.idCampo,
      onde: pendencia.numero
        ? t('prescricao.revisao.item', { numero: pendencia.numero })
        : t('prescricao.documento.orientacao'),
      oque: rotuloCampo(pendencia.campo),
      ir: () => onIrParaItem(pendencia),
    })),
    ...saldo.linhas
      .filter((linha) => linha.bloqueia)
      .map((linha) => ({
        id: `sncr-${linha.tipo}`,
        onde: `SNCR ${linha.tipo}`,
        // Bloco (RCE/RET) ou números avulsos (notificação): o mesmo termo da tela do SNCR.
        oque: t(
          isNotificacaoSncr(linha.tipo)
            ? 'prescricao.revisao.sncr_insuficiente_nr'
            : 'prescricao.revisao.sncr_insuficiente'
        ),
        // Só focar o "Retirar" lá embaixo parecia não fazer nada: abre a retirada direto.
        ir: () => saldo.retirar(linha.tipo),
      })),
    ...faltasEnvio.map((campo) => ({
      id: `envio-${campo}`,
      onde: t('prescricao.revisao.envio'),
      oque: t(`prescricao.envio.${campo}`).toLowerCase(),
      ir: () => focar(campo === 'telefone' ? ID_TELEFONE_ENVIO : ID_EMAIL_ENVIO),
    })),
  ]
  const bloqueada = pendencias.length > 0

  return (
    <Drawer
      show={aberto}
      onHide={onFechar}
      title={t('prescricao.revisao.titulo')}
      subtitle={t('prescricao.revisao.subtitulo')}
      restoreFocus={devolverFoco}
      className={styles.tema}
      bodyClassName='p-0'
      footer={
        <>
          <span
            id='revisao-rodape'
            className={`fw-semibold ${bloqueada ? styles.pendenciaErro : styles.textoSecundario}`}
          >
            {bloqueada
              ? t('prescricao.revisao.rodape_bloqueado')
              : t('prescricao.revisao.rodape_pronto', { quantidade: documentos.length })}
          </span>
          <Button
            className={`btn-primary ${styles.acaoBloqueada}`}
            ariaDisabled={bloqueada}
            ariaDescribedBy='revisao-rodape'
            onClick={() => (bloqueada ? pendencias[0].ir() : onAssinar())}
          >
            {t('prescricao.revisao.assinar', { quantidade: documentos.length })}
          </Button>
        </>
      }
    >
      <SecaoPendencias pendencias={pendencias} />
      <SecaoDocumentos documentos={documentos} />
      <SecaoSncr linhas={saldo.linhas} onRetirar={saldo.retirar} />
      <SecaoSus
        itensLme={itensLme}
        itensApac={itensApac}
        preferencias={sus}
        onChange={onSusChange}
      />
      <SecaoEnvio
        preferencias={envio}
        onChange={onEnvioChange}
        erros={{
          telefone: faltasEnvio.includes('telefone'),
          email: faltasEnvio.includes('email'),
        }}
      />
    </Drawer>
  )
}
